import React, { useState, useEffect } from 'react';
import { Mail, CreditCard, Database, Clock, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Activity, Link2, Hourglass } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLE = {
  operational: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-900/30', label: 'Operational' },
  pending: { icon: Hourglass, color: 'text-blue-400', bg: 'bg-blue-900/30', label: 'Pending' },
  degraded: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-900/30', label: 'Degraded' },
  'not-configured': { icon: AlertTriangle, color: 'text-gray-500', bg: 'bg-[#131C3A]', label: 'Not configured' },
  unknown: { icon: Clock, color: 'text-gray-400', bg: 'bg-[#131C3A]', label: 'Unknown' },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${s.bg} ${s.color}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const SystemHealthPage = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchHealth = async (showToast = false) => {
    try {
      setRefreshing(true);
      const r = await api.get('/admin/metrics/system-health');
      setHealth(r.data);
      setLastChecked(new Date());
      if (showToast) toast.success('Health check complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Health check failed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchHealth(false); }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-red-400" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="system-health-title">System Health</h1>
          <p className="text-slate-300">Live diagnostic snapshot of all backend services.</p>
          {lastChecked && (
            <p className="text-xs text-gray-500 mt-1">Last checked: {lastChecked.toLocaleString()}</p>
          )}
        </div>
        <Button variant="outline" onClick={() => fetchHealth(true)} disabled={refreshing} data-testid="refresh-system-health">
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Run Check
        </Button>
      </div>

      {/* Overall status */}
      <Card className="mb-6">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Overall Status</div>
            <div className="text-2xl font-bold text-slate-100">
              {health?.overall_status === 'operational' ? 'All systems operational' : 'Degraded — review below'}
            </div>
          </div>
          <StatusPill status={health?.overall_status} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SendGrid */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center gap-2"><Mail className="h-5 w-5" /> SendGrid</CardTitle>
              <StatusPill status={health?.sendgrid?.status} />
            </div>
            <CardDescription>Transactional email delivery</CardDescription>
          </CardHeader>
          <CardContent>
            {health?.sendgrid?.configured === false ? (
              <p className="text-sm text-slate-300">SendGrid API key not configured.</p>
            ) : health?.sendgrid?.status === 'operational' ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-2xl font-bold text-red-400" data-testid="sg-requests">{health.sendgrid.requests_24h ?? 0}</div><div className="text-xs text-gray-500">Requests 24h</div></div>
                <div><div className="text-2xl font-bold text-emerald-400">{health.sendgrid.delivered_24h ?? 0}</div><div className="text-xs text-gray-500">Delivered 24h</div></div>
                <div><div className="text-2xl font-bold text-orange-400">{health.sendgrid.bounces_24h ?? 0}</div><div className="text-xs text-gray-500">Bounces 24h</div></div>
                <div><div className="text-2xl font-bold text-amber-400">{health.sendgrid.blocks_24h ?? 0}</div><div className="text-xs text-gray-500">Blocks 24h</div></div>
              </div>
            ) : (
              <div className="bg-yellow-950/40 border border-yellow-800/50 rounded p-3 text-sm text-yellow-200">
                <strong>SendGrid stats unavailable.</strong>
                {health?.sendgrid?.http_code === 403 ? (
                  <p className="mt-1">The current API key only has Mail-Send permission. To unlock delivery stats here, add the <strong>Stats</strong> scope when you next rotate the key in SendGrid → Settings → API Keys.</p>
                ) : (
                  <p className="mt-1">Error: {health?.sendgrid?.error || `HTTP ${health?.sendgrid?.http_code}`}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stripe Webhook */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center gap-2"><CreditCard className="h-5 w-5" /> Stripe Webhook</CardTitle>
              <StatusPill status={health?.stripe_webhook?.status} />
            </div>
            <CardDescription>Payment webhook ingestion</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-300">API Key Configured</span><Badge variant={health?.stripe_webhook?.configured ? 'default' : 'secondary'}>{health?.stripe_webhook?.configured ? 'Yes' : 'No'}</Badge></div>
            <div className="flex justify-between"><span className="text-slate-300">Events 24h</span><strong data-testid="sw-events-24h">{health?.stripe_webhook?.events_24h ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-300">Last Event Type</span><span className="font-mono text-xs">{health?.stripe_webhook?.last_event_type || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-300">Last Event At</span><span className="text-xs">{fmtTime(health?.stripe_webhook?.last_event_at)}</span></div>
          </CardContent>
        </Card>

        {/* MongoDB */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center gap-2"><Database className="h-5 w-5" /> MongoDB</CardTitle>
              <StatusPill status={health?.mongodb?.status} />
            </div>
            <CardDescription>Database health & growth</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-300">Collections</span><strong>{health?.mongodb?.collections ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-300">Total Documents</span><strong>{(health?.mongodb?.documents ?? 0).toLocaleString()}</strong></div>
            <div className="flex justify-between"><span className="text-slate-300">Data Size</span><strong>{health?.mongodb?.data_size_mb ?? 0} MB</strong></div>
            <div className="flex justify-between"><span className="text-slate-300">Storage Size</span><strong>{health?.mongodb?.storage_size_mb ?? 0} MB</strong></div>
            {health?.mongodb?.key_counts && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-xs text-gray-500 mb-2">Top collections</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(health.mongodb.key_counts).map(([name, n]) => (
                    <span key={name} className="px-2 py-1 bg-[#131C3A] rounded text-xs">
                      <strong>{n.toLocaleString()}</strong> {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduler */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center gap-2"><Activity className="h-5 w-5" /> Background Scheduler</CardTitle>
              <StatusPill status={health?.scheduler?.status} />
            </div>
            <CardDescription>Signal runner + trial expiration checker</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-300">Last Signal Run</span><span className="text-xs" data-testid="sched-last-signal">{fmtTime(health?.scheduler?.last_signal_run_at)}</span></div>
            <div className="flex justify-between"><span className="text-slate-300">Last Trial Check</span><span className="text-xs" data-testid="sched-last-trial">{fmtTime(health?.scheduler?.last_trial_check_at)}</span></div>
            <p className="text-xs text-gray-500 pt-2">Signal scheduler ticks every 60s. Trial expiration check runs every 6h.</p>
          </CardContent>
        </Card>

        {/* iter55 — Link Branding TLS cert monitor */}
        <LinkBrandingCard
          data={health?.link_branding}
          onRefresh={() => fetchHealth(false)}
        />
      </div>
    </div>
  );
};

const LinkBrandingCard = ({ data, onRefresh }) => {
  const [checking, setChecking] = useState(false);

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const r = await api.post('/admin/metrics/link-branding/check-now');
      const state = r.data?.state;
      if (state === 'resolved') {
        toast.success("Let's Encrypt cert now active — click-tracking URLs are trusted.");
      } else if (state === 'pending') {
        toast.info('Still on SendGrid fallback wildcard. Cert not issued yet.');
      } else {
        toast.warning(`Check completed: ${state}`);
      }
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card data-testid="link-branding-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Link2 className="h-5 w-5" /> SendGrid Link Branding
          </CardTitle>
          <StatusPill status={data?.status} />
        </div>
        <CardDescription>
          TLS cert on click-tracking host — controls if email links show privacy warnings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-300">Target host</span>
          <code className="text-xs bg-[#131C3A] px-1.5 py-0.5 rounded" data-testid="lb-host">
            {data?.target_host || 'url1045.dotbox.ai'}
          </code>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-300">Cert presented</span>
          <code className="text-xs" data-testid="lb-cn">{data?.cert_cn || '—'}</code>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-300">SAN matches target</span>
          <Badge
            variant={data?.san_matches_target ? 'default' : 'secondary'}
            data-testid="lb-san-match"
          >
            {data?.san_matches_target ? 'Yes' : 'No'}
          </Badge>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-300">Last checked</span>
          <span className="text-xs" data-testid="lb-last-checked">
            {fmtTime(data?.last_checked_at)}
          </span>
        </div>
        {data?.notes && (
          <div className={`mt-3 p-2.5 rounded text-xs ${
            data?.state === 'resolved'
              ? 'bg-emerald-950/40 border border-emerald-800/50 text-emerald-200'
              : data?.state === 'pending'
                ? 'bg-blue-950/40 border border-blue-800/50 text-blue-200'
                : 'bg-yellow-950/40 border border-yellow-800/50 text-yellow-200'
          }`} data-testid="lb-notes">
            {data?.notes}
          </div>
        )}
        <div className="flex items-center justify-between pt-1">
          <p className="text-[10px] text-gray-500">
            Auto-check every 12h. Admins get notified on state change.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCheckNow}
            disabled={checking}
            data-testid="lb-check-now-btn"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${checking ? 'animate-spin' : ''}`} />
            Check now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemHealthPage;
